import { observable, computed, makeObservable, runInAction } from 'mobx';

export const CompletionType = {
    //to track that at least one of operations is still pending
    Pending: 0,
    //that operation was completed at least once
    Completed: 1
} as const;

export type CompletionType = typeof CompletionType[keyof typeof CompletionType];

export class PromiseCompletion {
    @observable.shallow private _promiseList: PromiseLike<void | {}>[] = [];
    @observable private _executedOnce: boolean = false;
    private _type: CompletionType;
    private _waiters: (() => void)[] = [];

    constructor(type: CompletionType = CompletionType.Pending) {
        this._type = type;
        makeObservable(this);
    }

    @computed
    get isCompleted() {
        let result = !this._promiseList.length;
        if (this._type === CompletionType.Completed) {
            result = result || this._executedOnce;
        }
        return result;
    }

    @computed
    get isPending() {
        return !!this._promiseList.length;
    }

    public subscribe(promise: PromiseLike<void | {}>) {
        if (this._promiseList.includes(promise)) {
            throw new Error('Promise is already registered!');
        }

        runInAction(() => {
            this._promiseList.push(promise);
        });
        
        // eslint-disable-next-line promise/catch-or-return
        promise.then(() => this._complete(promise), () => this._complete(promise));
    }

    private _complete(promise: PromiseLike<void | {}>) {
        window.setTimeout(() => {
            const index = this._promiseList.indexOf(promise);
            if (index === -1)
                throw new Error('Promise is not registered!');

            runInAction(() => {
                this._promiseList.splice(index, 1);
                this._executedOnce = true;

                if (!this._promiseList.length){
                    const waiters = this._waiters.slice(0);
                    this._waiters.length = 0;
                    waiters.forEach(w => w());
                }
            });
        }, 0);
    }

    public wait(): Promise<void>{
        if (!this._promiseList.length && this._executedOnce){
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            this._waiters.push(resolve);
        });
    }
}
